'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const LocationIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);
const BellIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);
const CoinIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="12" r="10" fill="#f59e0b" opacity="0.9"/>
    <text x="12" y="16" textAnchor="middle" fontSize="10" fill="#7c2d12" fontWeight="bold">G</text>
  </svg>
);
const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);
const MenuIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);

interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  priority?: string;
  read: boolean;
  created_at: string;
}

interface TopBarProps {
  sidebarCollapsed: boolean;
  onMobileMenuToggle?: () => void;
}

function NotificationDropdown({ userId, onClose }: { userId: string; onClose: () => void }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread' | 'urgent'>('all');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const supabase = createClient();
      const { data } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(20);
      setNotifications(data || []);
      setLoading(false);
    };
    load();
  }, [userId]);

  const markRead = async (id: string) => {
    const supabase = createClient();
    await supabase.from('notifications').update({ read: true }).eq('id', id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllRead = async () => {
    const supabase = createClient();
    const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
    if (!unreadIds.length) return;
    await supabase.from('notifications').update({ read: true }).in('id', unreadIds);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const typeIcon = (type: string, priority?: string) => {
    if (priority === 'urgent') return '🚨';
    if (priority === 'high') return '⚡';
    if (type === 'success') return '✅';
    if (type === 'error') return '❌';
    if (type === 'warning') return '⚠️';
    return '🔔';
  };

  const priorityBorder = (priority?: string) => {
    if (priority === 'urgent') return 'border-l-2 border-l-red-500';
    if (priority === 'high') return 'border-l-2 border-l-yellow-500';
    return '';
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const filtered = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'urgent') return n.priority === 'urgent' || n.priority === 'high';
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;
  const urgentCount = notifications.filter(n => !n.read && (n.priority === 'urgent' || n.priority === 'high')).length;

  return (
    <div className="absolute right-0 top-full mt-2 w-80 bg-[#0d0d1a] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
        <div>
          <div className="text-xs font-semibold text-white">Notifications</div>
          <div className="flex items-center gap-2 mt-0.5">
            {unreadCount > 0 && <div className="text-[10px] text-white/40">{unreadCount} unread</div>}
            {urgentCount > 0 && <div className="text-[10px] text-red-400 font-semibold">🚨 {urgentCount} urgent</div>}
          </div>
        </div>
        {unreadCount > 0 && (
          <button onClick={markAllRead} className="text-[10px] text-gxg-purple2 hover:text-white transition-colors">
            Mark all read
          </button>
        )}
      </div>

      {/* Priority Filter Tabs */}
      <div className="flex border-b border-white/8">
        {(['all', 'unread', 'urgent'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`flex-1 text-[10px] py-2 capitalize transition-colors ${
              filter === f ? 'text-gxg-purple2 border-b border-gxg-purple2' : 'text-white/35 hover:text-white/60'
            }`}
          >
            {f}
            {f === 'unread' && unreadCount > 0 && <span className="ml-1 text-[9px] bg-purple-500/30 rounded-full px-1">{unreadCount}</span>}
            {f === 'urgent' && urgentCount > 0 && <span className="ml-1 text-[9px] bg-red-500/30 rounded-full px-1">{urgentCount}</span>}
          </button>
        ))}
      </div>

      <div className="max-h-72 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <svg className="animate-spin w-4 h-4 text-gxg-purple2" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-6 text-white/30 text-xs">
            {filter === 'urgent' ? 'No urgent notifications' : filter === 'unread' ? 'All caught up!' : 'No notifications yet'}
          </div>
        ) : (
          filtered.map(n => (
            <div
              key={n.id}
              onClick={() => markRead(n.id)}
              className={`flex items-start gap-3 px-4 py-3 border-b border-white/4 last:border-0 hover:bg-white/3 transition-colors cursor-pointer ${!n.read ? 'bg-purple-500/5' : ''} ${priorityBorder(n.priority)}`}
            >
              <span className="text-sm flex-shrink-0 mt-0.5">{typeIcon(n.type, n.priority)}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <div className={`text-xs font-semibold ${!n.read ? 'text-white' : 'text-white/70'}`}>{n.title}</div>
                  {n.priority === 'urgent' && <span className="text-[8px] bg-red-500/20 text-red-400 rounded px-1 py-0.5 border border-red-500/30">URGENT</span>}
                  {n.priority === 'high' && <span className="text-[8px] bg-yellow-500/20 text-yellow-400 rounded px-1 py-0.5 border border-yellow-500/30">HIGH</span>}
                </div>
                <div className="text-[10px] text-white/40 mt-0.5 line-clamp-2">{n.message}</div>
                <div className="text-[10px] text-white/25 mt-1">{formatTime(n.created_at)}</div>
              </div>
              {!n.read && <div className="w-2 h-2 rounded-full bg-gxg-purple2 flex-shrink-0 mt-1.5" />}
            </div>
          ))
        )}
      </div>
      <div className="px-4 py-2 border-t border-white/8">
        <Link href="/passport" onClick={onClose} className="text-[11px] text-gxg-purple2 hover:text-white transition-colors">
          View Passport →
        </Link>
      </div>
    </div>
  );
}

export default function TopBar({ sidebarCollapsed, onMobileMenuToggle }: TopBarProps) {
  const [checkedIn, setCheckedIn] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user, profile, loading, signOut, canAccessAdmin } = useAuth();
  const router = useRouter();
  const sidebarWidth = sidebarCollapsed ? 64 : 210;

  const handleSignOut = async () => {
    setUserMenuOpen(false);
    await signOut();
    router.push('/login');
  };

  const displayName = profile?.full_name || profile?.username || (user?.email ? user.email.split('@')[0] : 'Guest');
  const displayInitials = displayName.slice(0, 2).toUpperCase();
  const displayLevel = profile?.level ?? 1;
  const displayCoins = profile?.gxg_coins ?? 0;

  const loadUnreadCount = useCallback(async () => {
    if (!user?.id) return;
    try {
      const supabase = createClient();
      const { count } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('read', false);
      setUnreadCount(count || 0);
    } catch {
      // silently fail
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      loadUnreadCount();
      const interval = setInterval(loadUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user?.id, loadUnreadCount]);

  return (
    <div
      className="gxg-topbar"
      style={{ left: sidebarWidth }}
    >
      {/* Mobile menu button */}
      <button
        className="md:hidden text-white/50 hover:text-white/80 transition-colors mr-2 flex-shrink-0"
        onClick={onMobileMenuToggle}
        aria-label="Toggle menu"
      >
        <MenuIcon />
      </button>

      {/* Search */}
      <div className="gxg-search flex-1 max-w-sm">
        <span className="text-white/30"><SearchIcon /></span>
        <input type="text" placeholder="Search games, missions, players..." />
        <span className="text-[10px] font-mono text-white/25 bg-white/5 px-1.5 py-0.5 rounded border border-white/10 flex-shrink-0 hidden sm:block">⌘K</span>
      </div>

      <div className="flex-1" />

      {/* Check In */}
      <button
        className="checkin-btn hidden sm:flex"
        onClick={() => setCheckedIn(!checkedIn)}
      >
        <LocationIcon />
        <span>{checkedIn ? 'Checked In ✓' : 'Check In'}</span>
      </button>

      {/* Notification Bell */}
      <div className="relative flex-shrink-0">
        <button
          onClick={() => { setNotifOpen(!notifOpen); if (!notifOpen && user?.id) loadUnreadCount(); }}
          className="relative w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white/5 transition-colors text-white/60 hover:text-white/90"
        >
          <BellIcon />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full text-[9px] font-bold text-white flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
        {notifOpen && user?.id && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
            <div className="relative z-50">
              <NotificationDropdown
                userId={user.id}
                onClose={() => { setNotifOpen(false); loadUnreadCount(); }}
              />
            </div>
          </>
        )}
      </div>

      {/* Coins — only for logged-in users */}
      {user && (
        <div className="hidden sm:flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 flex-shrink-0">
          <CoinIcon />
          <span className="text-sm font-semibold text-gxg-gold font-mono">{displayCoins.toLocaleString()}</span>
        </div>
      )}

      {/* User Profile / Auth */}
      {loading ? (
        <div className="w-9 h-9 rounded-full bg-white/5 animate-pulse flex-shrink-0" />
      ) : user ? (
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 hover:border-purple-500/30 transition-colors"
          >
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
              {displayInitials}
            </div>
            <div className="hidden sm:block text-left">
              <div className="text-xs font-semibold text-white/90 leading-none">{displayName}</div>
              <div className="text-[10px] text-white/40 leading-none mt-0.5">Level {displayLevel}</div>
            </div>
            <span className="text-white/30 hidden sm:block"><ChevronDownIcon /></span>
          </button>

          {userMenuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
              <div className="absolute right-0 top-full mt-2 w-48 bg-[#0d0d1a] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-white/5">
                  <div className="text-xs font-semibold text-white/80">{displayName}</div>
                  <div className="text-[10px] text-white/40 capitalize">{profile?.role?.replace('_', ' ') ?? 'Guest'}</div>
                </div>
                <div className="py-1">
                  <Link
                    href="/passport"
                    onClick={() => setUserMenuOpen(false)}
                    className="block w-full text-left px-4 py-2 text-xs text-white/60 hover:text-white/90 hover:bg-white/5 transition-colors"
                  >
                    My Passport
                  </Link>
                  {canAccessAdmin() && (
                    <Link
                      href="/admin"
                      onClick={() => setUserMenuOpen(false)}
                      className="block w-full text-left px-4 py-2 text-xs text-gxg-purple2 hover:bg-purple-500/10 transition-colors"
                    >
                      Admin Portal
                    </Link>
                  )}
                  <button
                    onClick={handleSignOut}
                    className="w-full text-left px-4 py-2 text-xs text-red-400 hover:bg-red-500/10 transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => router.push('/login')}
            className="px-3 py-1.5 text-xs text-white/60 hover:text-white/90 border border-white/10 hover:border-white/20 rounded-lg transition-colors"
          >
            Sign In
          </button>
          <button
            onClick={() => router.push('/signup')}
            className="px-3 py-1.5 text-xs bg-purple-600/20 text-gxg-purple2 border border-purple-500/30 hover:bg-purple-600/30 rounded-lg transition-colors hidden sm:block"
          >
            Join GXG
          </button>
        </div>
      )}
    </div>
  );
}
